import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../components/ui/dialog";
import type { RoomState } from "ghost-server";
import { durationInputToSeconds, formatSecondsAsDuration, secondsToDurationInput } from "../../../lib/duration";

export function StatesSection() {
	const [states, setStates] = useState<RoomState[]>([]);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState<RoomState | null>(null);
	const [stateName, setStateName] = useState("");
	const [transitionAfter, setTransitionAfter] = useState("");
	const [color, setColor] = useState<string>("");
	const [confirmValue, setConfirmValue] = useState<string | null>(null);

	async function load() {
		const { data } = await api.admin.states.all.get();
		if (data) setStates(data);
	}

	useEffect(() => {
		load();
	}, []);

	function openAdd() {
		setEditing(null);
		setStateName("");
		setTransitionAfter("00:00:00");
		setColor("#00ffcc");
		setOpen(true);
	}

	function openEdit(state: RoomState) {
		setEditing(state);
		setStateName(state.state);
		setTransitionAfter(secondsToDurationInput(state.transitionAfterSeconds));
		setColor(state.color ?? "#00ffcc");
		setOpen(true);
	}

	async function handleUpsert() {
		const state = editing?.state ?? stateName.trim();
		const transitionAfterSeconds = durationInputToSeconds(transitionAfter);
		if (!state) return;
		if (transitionAfterSeconds === undefined) return;
		await api.admin.states.upsert.put({
			state,
			transitionAfterSeconds,
			color,
		});
		setOpen(false);
		load();
	}

	async function handleDelete(value: string) {
		await api.admin.states.delete.delete({ value });
		setConfirmValue(null);
		load();
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-xs text-[#333] font-mono">{states.length} states</p>
				<Dialog
					open={open}
					onOpenChange={(o) => {
						setOpen(o);
						if (!o) {
							setEditing(null);
							setStateName("");
							setTransitionAfter("");
						}
					}}
				>
					<DialogTrigger asChild>
						<Button size="sm" onClick={openAdd}>
							+ add state
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{editing ? `Edit State — ${editing.state}` : "Add State"}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>State Name</Label>
								<Input
									value={stateName}
									onChange={(e) => setStateName(e.target.value)}
									placeholder="e.g. warning"
									disabled={!!editing}
								/>
								<Label>Color</Label>
								<Input
									type="color"
									value={color}
									onChange={(e) => setColor(e.target.value)}
									className="w-16 h-8 p-0 border-none bg-transparent cursor-pointer"
								/>
							</div>
							<div className="space-y-2">
								<Label>Transition After</Label>
								<Input
									type="text"
									value={transitionAfter}
									onChange={(e) => setTransitionAfter(e.target.value)}
									placeholder="00:00:00"
									onKeyDown={(e) => e.key === "Enter" && handleUpsert()}
								/>
							</div>
						</div>
						<DialogFooter>
							<DialogClose asChild>
								<Button variant="ghost" size="sm">
									cancel
								</Button>
							</DialogClose>
							<Button size="sm" onClick={handleUpsert}>
								{editing ? "update" : "create"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>State</TableHead>
						<TableHead>Transition After</TableHead>
						<TableHead>Color</TableHead>
						<TableHead className="w-32" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{states.map((state) => (
						<TableRow key={state.state}>
							<TableCell className="text-[#00ffcc]">{state.state}</TableCell>
							<TableCell>{formatSecondsAsDuration(state.transitionAfterSeconds)}</TableCell>
							<TableCell>
								<span
									style={{
										background: state.color ?? "#00ffcc",
										display: "inline-block",
										width: 24,
										height: 24,
										borderRadius: 4,
										border: "1px solid #ccc",
									}}
								/>
							</TableCell>
							<TableCell>
								{confirmValue === state.state ? (
									<div className="flex gap-1">
										<Button size="sm" variant="destructive" onClick={() => handleDelete(state.state)}>
											confirm
										</Button>
										<Button size="sm" variant="ghost" onClick={() => setConfirmValue(null)}>
											cancel
										</Button>
									</div>
								) : (
									<div className="flex gap-1">
										<Button size="sm" variant="ghost" onClick={() => openEdit(state)}>
											edit
										</Button>
										<Button size="sm" variant="ghost" onClick={() => setConfirmValue(state.state)}>
											delete
										</Button>
									</div>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
